import ApiErrorBanner from '@/components/ApiErrorBanner';
import { ApiErrorType } from '@/components/ApiErrorBanner/ApiErrorBanner';
import StudyUsers from '@/components/Studies/Show/Users';
import { DateRenderer, DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import Link from '@/components/UI/Link';
import APITable from '@/components/UI/Table/Reusable/APITable';
import type { TanStackColumnDef, FilterOption } from '@/components/UI/Table/TableComponent.model';
import { _notNil } from '@/littledash';
import type { Study } from '@/model/Study.model';
import { statusToTextMap } from '@/referenceData/study/status';
import { notAborted } from '@/support/Hooks/fetch/useAbortController';
import { api as apiRoutes, web as webRoutes } from '@/support/route';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { StudyTableProps } from './Dashboard.model';
import { generateColumns, generateStudiesMetadataColumns, studiesfilterOptions } from './Dashboard.utils';
import { createMultiSelectDoubleEncodingProcessor } from '@/utils/TableFilter.utils';
import { useFetchCollection } from '@/support/Hooks/fetch';
import type { StudyForm } from '@/model/StudyForm.model';
import Truncate from '@/components/UI/Truncate/Truncate';
import BenchlingEntityLink from '@/components/UI/BenchlingEntityLink/BenchlingEntityLink';

const initColumns: Array<TanStackColumnDef<Study>> = [
  {
    id: 'name',
    header: 'Name',
    accessorKey: 'name',
    enableSorting: true,
    isVisible: true,
    cell: (props) => {
      const original = props.row.original;
      const hasBenchlingSync = original.synced_to_benchling === true;
      return (
        <div className="flex items-center">
          <BenchlingEntityLink
            entityType="studies"
            apiId={original.api_id}
            benchlingEntityWebUrl={original.benchling_entity_web_url}
            visible={hasBenchlingSync}
          />
          <Link
            className="link w-100"
            to={webRoutes('studies.show', {
              id: original.id,
            })}
          >
            <Truncate tooltip={original.name}>{original.name}</Truncate>
          </Link>
        </div>
      );
    },
  },
  {
    id: 'code',
    header: 'Study Code',
    accessorKey: 'code',
    enableSorting: true,
    isVisible: false,
  },
  {
    id: 'benchling_entity_id',
    header: 'Registry API ID',
    accessorKey: 'benchling_entity_id',
    enableSorting: false,
    isVisible: false,
    cell: (props) => props.row.original.benchling_entity_id ?? '-',
  },
  {
    id: 'type',
    header: 'Type',
    isVisible: true,
    enableSorting: true,
    accessorKey: 'settings.title' as keyof Study,
  },
  {
    id: 'team',
    header: 'Team',
    isVisible: true,
    enableSorting: false,
    accessorKey: 'users',
    cell: (props) => <StudyUsers study={props.row.original} />,
  },
  {
    id: 'status',
    header: 'Status',
    accessorKey: 'status',
    isVisible: true,
    enableSorting: true,
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    cell: (props) => statusToTextMap[props.row.original.status] || '',
  },
  {
    id: 'created_at',
    header: 'Created',
    accessorKey: 'created_at',
    enableSorting: true,
    isVisible: true,
    cell: (props) => <DateTimeRenderer value={props.row.original.created_at} />,
  },
  {
    id: 'started_on',
    header: 'Start date',
    accessorKey: 'started_on',
    enableSorting: true,
    isVisible: true,
    cell: (props) => <DateRenderer value={props.row.original.started_on} />,
  },
];

const ActiveTable: FC<StudyTableProps> = ({ studyTypes, studyMetadataFilters, hasStudyCode }) => {
  const [filterOptions, setFilterOptions] = useState<Array<FilterOption>>([]);
  const [columns, setColumns] = useState<Array<TanStackColumnDef<Study>>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [apiError, setAPIError] = useState<boolean>(false);
  const navigate = useNavigate();

  const { collection: studyForms, collectionLoading: studyFormsFetching } = useFetchCollection<StudyForm>({
    collectionType: 'teamStudiesForms',
    queryParams: { perPage: -1 },
  });

  useEffect(() => {
    if (_notNil(studyTypes)) {
      setFilterOptions(studiesfilterOptions(studyTypes, studyMetadataFilters));
    }
  }, [studyTypes, studyMetadataFilters]);

  useEffect(() => {
    if (_notNil(studyTypes) && _notNil(studyMetadataFilters) && !studyFormsFetching && hasStudyCode !== undefined) {
      generateStudiesMetadataColumns(studyMetadataFilters)
        .then((metadata) => {
          const baseColumns = initColumns.map((col) => (col.id === 'code' ? { ...col, isVisible: hasStudyCode } : col));
          setColumns(
            generateColumns(
              baseColumns,
              metadata.map((m) => ({ id: m.id, header: m.header ?? '', slug: m.slug })),
              navigate,
              studyForms
            )
          );
          setLoading(false);
        })
        .catch((error) => {
          if (notAborted(error)) {
            setLoading(false);
            setAPIError(error);
          }
        });
    }
  }, [studyTypes, studyMetadataFilters, studyFormsFetching, hasStudyCode]);

  if (loading) {
    return null;
  }

  return (
    <>
      {apiError ? (
        <ApiErrorBanner className="mb4" errorType={ApiErrorType.FETCH} error={apiError} />
      ) : (
        <div className="pa4">
          <APITable
            columns={columns}
            apiInfo={{ type: 'legacyInternalApi', route: apiRoutes('studies.active') }}
            reduxTableName="dashboardActive"
            filterOptions={filterOptions}
            showViewSelector
            customFilterProcessor={createMultiSelectDoubleEncodingProcessor(studyMetadataFilters ?? [])}
            searchPlaceholderText="Search by study name or study code"
          />
        </div>
      )}
    </>
  );
};

export default ActiveTable;
